import { z } from 'zod';
import type { NativeBilling, StoreTransaction } from './native-platform.js';
import { STORE_PRO_PRODUCTS } from '../shared/store-billing.js';

const configuration = z.object({ accountToken: z.uuid(), productId: z.string(), active: z.boolean() });
const verifiedPurchase = z.object({ active: z.boolean(), purchasedAccessActive: z.boolean() });
export class StorePurchaseError extends Error {
  constructor(readonly status: number, message: string) { super(message); }
}
export async function storeConfiguration(billing: NativeBilling) {
  const response = await fetch(`/api/store/config?store=${billing.store}`, { credentials: 'same-origin', cache: 'no-store' });
  if (!response.ok) throw new StorePurchaseError(response.status, response.status === 401
    ? 'Sign in to buy or restore Pro.' : 'Store purchases are not available yet.');
  const config = configuration.parse(await response.json());
  if (config.productId !== STORE_PRO_PRODUCTS[billing.store]) throw new Error('The Pro subscription is unavailable.');
  return config;
}
export async function verifyStoreTransactions(billing: NativeBilling, transactions: StoreTransaction[]): Promise<boolean> {
  let active = false;
  for (const transaction of transactions) {
    const response = await fetch('/api/store/verify', {
      method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ store: billing.store, token: transaction.token, sandbox: transaction.sandbox }),
    });
    if (!response.ok) throw new StorePurchaseError(response.status, response.status === 409
      ? 'This purchase belongs to another Draw on a Map account. Sign in with that account to restore it.'
      : 'The purchase could not be checked. Use Restore purchases to try again.');
    const result = verifiedPurchase.parse(await response.json());
    // Finish only after the server has saved and verified this transaction.
    await billing.finish(transaction.id);
    active ||= result.active;
  }
  if (active) window.dispatchEvent(new Event('store-access-changed'));
  return active;
}

let recovery: { status: 'idle' } | { status: 'recovering'; result: Promise<void> } = { status: 'idle' };
export function recoverStorePurchases(): Promise<void> {
  if (recovery.status === 'recovering') return recovery.result;
  const result = (async () => {
    const billing = window.drawOnAMapNative?.billing;
    if (!billing) return;
    await storeConfiguration(billing);
    const result = await billing.current();
    await verifyStoreTransactions(billing, result.transactions);
  })().catch(() => { /* Keep unfinished purchases for the next foreground or explicit restore. */ })
    .finally(() => { recovery = { status: 'idle' }; });
  recovery = { status: 'recovering', result };
  return result;
}
